'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const services = [
{
  href: '/services/hvac',
  badge: '24/7 Emergency',
  title: 'HVAC Repair',
  desc: 'Stay cool in summer, warm in winter.',
  img: 'https://images.unsplash.com/photo-1532316191261-35afb35c7346',
  alt: 'HVAC technician servicing air conditioning unit on rooftop, bright sky',
  colSpan: 'lg:col-span-2 lg:row-span-2',
  minH: 'min-h-[420px] lg:min-h-[520px]'
},
{
  href: '/services/locksmith',
  badge: '30-Min Response',
  title: 'Locksmith & Car Keys',
  desc: 'Locked out? We arrive in under 30 min.',
  img: 'https://img.rocket.new/generatedImages/rocket_gen_img_13627dc9a-1769266065442.png',
  alt: 'Locksmith programming car key fob in garage, close-up tools on workbench',
  colSpan: 'lg:col-span-2',
  minH: 'min-h-[240px]'
},
{
  href: '/services/roofing',
  badge: 'Free Inspection',
  title: 'Roofing',
  desc: 'Leak-proof. Storm-ready.',
  img: 'https://img.rocket.new/generatedImages/rocket_gen_img_112e9c098-1772785063829.png',
  alt: 'Roofer installing asphalt shingles on residential home, bright afternoon',
  colSpan: 'lg:col-span-1',
  minH: 'min-h-[240px]'
},
{
  href: '/services/plumbing',
  badge: 'Licensed & Insured',
  title: 'Plumbing',
  desc: 'No drip too small, no burst too big.',
  img: 'https://img.rocket.new/generatedImages/rocket_gen_img_11fcbce8b-1772357500449.png',
  alt: 'Plumber repairing pipe under sink in well-lit kitchen, wrench in hand',
  colSpan: 'lg:col-span-1',
  minH: 'min-h-[240px]'
}];


export default function ServicesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('[data-animate]');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-24 px-4 md:px-6 bg-paper" ref={sectionRef}>
      <div className="max-w-6xl mx-auto">
        <div
          data-animate
          className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6"
          style={{ opacity: 0, transform: 'translateY(28px)', transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1)' }}>
          
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-ink-faint mb-4">What We Fix</p>
            <h2
              className="font-sans font-extrabold leading-none text-ink"
              style={{ fontSize: 'clamp(2.4rem, 6vw, 5rem)', letterSpacing: '-0.04em' }}>
              
              Four Services,<br />
              <span className="font-serif italic font-normal text-ink-muted">One Trusted Team.</span>
            </h2>
          </div>
          <p className="text-ink-muted max-w-xs text-base leading-relaxed md:text-right">
            Tap any card to call the dedicated specialist for that service directly.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-auto">
          {services.map((service, i) =>
          <Link
            key={service.href}
            href={service.href}
            className={`service-card ${service.colSpan} ${service.minH} block`}
            data-animate
            style={{
              opacity: 0,
              transform: 'translateY(32px)',
              transition: `opacity 0.6s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s, transform 0.6s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s`
            }}
            aria-label={`Learn more about ${service.title}`}>
            
              <div className="relative" style={{ width: '100%', height: '100%' }}>
                <img
                alt={service.alt}
                loading="lazy"
                className="object-cover"
                style={{ position: 'absolute', height: '100%', width: '100%', left: 0, top: 0, right: 0, bottom: 0, objectFit: 'cover' }}
                src={service.img} />
              
              </div>
              <div className="service-card-content">
                <span className="inline-block bg-paper/90 text-ink text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full mb-3">
                  {service.badge}
                </span>
                <h3 className="text-white font-bold text-xl md:text-2xl leading-tight mb-1">{service.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed mb-4 hidden sm:block">{service.desc}</p>
                <div className="wa-btn wa-btn-white inline-flex items-center gap-2 text-xs">View Service &rarr;</div>
              </div>
            </Link>
          )}
        </div>
      </div>
    </section>);

}